import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '../components/popover';
import { ChevronsUpDownIcon, CheckIcon, SearchIcon } from 'lucide-react';
import { Input } from '../components/input';
import { cn } from '../lib/utils';

export interface AutocompleteOption {
  value: string;
  label: string;
}

interface AutocompleteSelectProps {
  value?: string;
  onChange: (value: string) => void;
  options: AutocompleteOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  creatable?: boolean;
  disabled?: boolean;
  className?: string;
  /**
   * Keep the list open after a pick so several values can be chosen in a row.
   * The parent still owns the resulting collection -- this only changes when
   * the popover closes.
   */
  multiple?: boolean;
}

const AutocompleteSelect: React.FC<AutocompleteSelectProps> = ({
  value,
  onChange,
  options,
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  emptyText = 'No results found.',
  creatable = false,
  disabled = false,
  className,
  multiple = false,
}) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus the search input when the popover opens
  useEffect(() => {
    if (open && inputRef.current) {
      // Slight delay to let the popover animation complete
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [open]);

  // Find the currently selected option
  const selectedOption = useMemo(() => {
    const match = options.find((opt) => opt.value === value);
    if (match) return match;
    // If the value isn't in the original options (user typed custom), show raw value
    return value ? { value, label: value } : undefined;
  }, [options, value]);

  // Determine which options to display and whether to show "create" option
  const displayData = useMemo(() => {
    if (!searchQuery.trim()) {
      return {
        filtered: options,
        showCreate: false,
      };
    }

    const q = searchQuery.trim().toLowerCase();
    const filtered = options.filter(
      (opt) =>
        opt.label.toLowerCase().includes(q) ||
        opt.value.toLowerCase().includes(q)
    );

    const exactMatch = options.some(
      (opt) =>
        opt.label.toLowerCase() === q ||
        opt.value.toLowerCase() === q
    );

    const showCreate = creatable && !exactMatch;

    return { filtered, showCreate };
  }, [options, searchQuery, creatable]);

  const handleSelect = useCallback(
    (optionValue: string) => {
      onChange(optionValue);
      // Picking one of many: stay open, and keep the query so the rest of the
      // matches for that search are still on screen.
      if (multiple) return;
      setOpen(false);
      setSearchQuery('');
    },
    [onChange, multiple]
  );

  // Enter takes the top match (or the create option), so a run of values can be
  // added by typing without reaching for the mouse between each one.
  const handleSearchKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      const [first] = displayData.filtered;
      if (first) handleSelect(first.value);
      else if (displayData.showCreate) handleSelect(searchQuery.trim());
    },
    [displayData, handleSelect, searchQuery]
  );

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      setOpen(newOpen);
      if (!newOpen) {
        setSearchQuery('');
      }
    },
    []
  );

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <div
          role="combobox"
          aria-expanded={open}
          className={cn(
            'flex w-full min-w-0 cursor-pointer items-center justify-between rounded-[6px] border p-[9px] text-caption2 transition-[color,box-shadow] outline-none',
            'shadow-[inset_0.38px_-0.38px_3.02px_0px_rgba(0,0,0,0.12)]',
            'border-input bg-transparent',
            disabled && 'pointer-events-none opacity-50',
            className
          )}
          onClick={() => !disabled && setOpen(!open)}
        >
          <span className={cn('truncate', !selectedOption && 'text-secondary')}>
            {selectedOption?.label || placeholder}
          </span>
          <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </div>
      </PopoverTrigger>

      <PopoverContent
        className="w-[--radix-popover-trigger-width] p-0"
        align="start"
        sideOffset={4}
      >
        {/* Search input */}
        <div className="flex items-center gap-2 border-b px-3 py-2">
          <SearchIcon className="h-4 w-4 shrink-0 opacity-50" />
          <input
            ref={inputRef}
            type="text"
            className="flex h-8 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            placeholder={searchPlaceholder}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleSearchKeyDown}
          />
        </div>

        {/* Options list */}
        <div className="max-h-[260px] overflow-y-auto py-1">
          {/* Create option */}
          {displayData.showCreate && (
            <div
              className="relative flex cursor-pointer items-center gap-2 rounded-sm px-6 py-1.5 text-sm italic text-muted-foreground hover:bg-accent hover:text-accent-foreground"
              onClick={() => handleSelect(searchQuery.trim())}
            >
              Use &ldquo;{searchQuery.trim()}&rdquo;
            </div>
          )}

          {/* Filtered options */}
          {displayData.filtered.length === 0 && !displayData.showCreate && (
            <div className="py-6 text-center text-sm text-muted-foreground">
              {emptyText}
            </div>
          )}

          {displayData.filtered.map((option) => (
            <div
              key={option.value}
              className={cn(
                'relative flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground',
                value === option.value && 'bg-accent/50'
              )}
              onClick={() => handleSelect(option.value)}
            >
              <CheckIcon
                className={cn(
                  'h-4 w-4 shrink-0',
                  value === option.value ? 'opacity-100' : 'opacity-0'
                )}
              />
              <span className="truncate">{option.label}</span>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export { AutocompleteSelect };
